import React, { useEffect, useRef, useState } from "react";
import { Loader, Mail } from "react-feather";
import { ReactComponent as Close } from "../../assets/icons/x.svg";
import { FormTextInput } from "../../Components/FormTextInput";
import { sendSignInCode, signInWithCode } from "../../utility/signInCode";
import { useCheckoutT } from "../../i18n/checkout";
import "./SignInForm.css";

const EMAIL = /\S+@\S+\.\S+/;
// Seconds before "Send a new code" works again.
const RESEND_AFTER = 30;

// "Email me a sign-in code" in the sign-in window (SignInForm): the email
// (already typed, or typed here), then the 6-digit code we email, which signs
// the person in (utility/signInCode). For people who don't remember their
// password; the server limits codes and tries (functions SignIn/index.js).
export default function EmailCodeSignIn({ email, setEmail, onSignedIn, onUsePassword, onClose }) {
  const { t, lang } = useCheckoutT();
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState("");
  // { key } of the message under the field.
  const [problem, setProblem] = useState(null);
  const [wait, setWait] = useState(0);
  const codeRef = useRef(null);
  // The last code sent to the server, so a new six digits go by themselves.
  const tried = useRef("");

  useEffect(() => {
    if (wait <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  const send = async () => {
    const to = String(email || "").trim().toLowerCase();
    if (!EMAIL.test(to)) {
      setProblem({ key: "account.invalidEmailFormat" });
      return;
    }
    setBusy("send");
    setProblem(null);
    try {
      await sendSignInCode(to, lang);
      setSentTo(to);
      setCode("");
      setWait(RESEND_AFTER);
      setTimeout(() => codeRef.current && codeRef.current.focus(), 50);
    } catch (e) {
      setProblem({ key: e.message === "too-many" ? "account.code.tooMany" : "account.codeSendError" });
    } finally {
      setBusy("");
    }
  };

  // Straight to the code when the email was typed before choosing this.
  useEffect(() => {
    if (EMAIL.test(String(email || "").trim())) {
      send();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verify = async (value = code) => {
    if (value.length !== 6 || busy) {
      return;
    }
    tried.current = value;
    setBusy("verify");
    setProblem(null);
    try {
      const credential = await signInWithCode(sentTo, value);
      onSignedIn(credential);
    } catch (e) {
      const key = {
        invalid: "account.code.invalid",
        expired: "account.code.expired",
        "too-many": "account.code.tooMany",
        disabled: "account.signIn.disabled",
      }[e.message] || "account.code.error";
      setProblem({ key });
      setBusy("");
    }
  };

  const typeCode = (value) => {
    const digits = String(value).replace(/\D/g, "").slice(0, 6);
    setCode(digits);
    setProblem(null);
    // Six digits (typed, pasted or filled in from the email) sign in.
    if (digits.length === 6 && digits !== tried.current) {
      verify(digits);
    }
  };

  return (
    <div className="signInForm" data-testid="email-code-sign-in">
      <div className="signInFormHeader">
        <h2 className="signInFormTitle">{t("account.code.title")}</h2>
        <Close className="signInFormIcon" onClick={onClose} />
      </div>

      {!sentTo ? (
        <>
          <p className="signInCodeText">{t("account.code.intro")}</p>
          <div className="signInFormContainer">
            <FormTextInput
              email={true}
              incorrect={Boolean(problem)}
              placeholder={t("field.email")}
              clearInputIcon={true}
              value={email}
              onChange={(value) => { setEmail(value); setProblem(null); }}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
          </div>
          {problem && <span className="signInFormInputValidation" role="alert">{t(problem.key)}</span>}
          <button className="signInFormButton" data-action="send-code" disabled={busy === "send" || String(email || "").trim() === ""} onClick={send}>
            {t(busy === "send" ? "account.code.sending" : "account.code.send")}
            {busy === "send" && <Loader className="btnLoader" />}
          </button>
        </>
      ) : (
        <>
          <p className="signInCodeText">
            <Mail className="signInCodeMail" aria-hidden="true" />
            <span>{t("account.code.sentTo")} <b>{sentTo}</b>. {t("account.code.expires")}</span>
          </p>
          <input
            ref={codeRef}
            className={`signInCodeInput${problem ? " signInCodeInputError" : ""}`}
            data-testid="sign-in-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            aria-label={t("account.code.label")}
            value={code}
            onChange={(e) => typeCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && verify()}
          />
          {problem && <span className="signInFormInputValidation" role="alert">{t(problem.key)}</span>}
          <button className="signInFormButton" data-action="verify-code" disabled={code.length !== 6 || busy === "verify"} onClick={() => verify()}>
            {t(busy === "verify" ? "account.signIn.signing" : "account.signIn.button")}
            {busy === "verify" && <Loader className="btnLoader" />}
          </button>
          <div className="signInCodeLinks">
            <button type="button" className="signInCodeLink" data-action="resend-code" disabled={wait > 0 || busy === "send"} onClick={send}>
              {wait > 0 ? t("account.code.resendIn", { seconds: wait }) : t("account.code.resend")}
            </button>
            <button type="button" className="signInCodeLink" onClick={() => { setSentTo(""); setProblem(null); }}>
              {t("account.code.otherEmail")}
            </button>
          </div>
        </>
      )}

      <div className="signInFormDontHaveAccount">
        <button type="button" className="signInCodeLink" data-action="use-password" onClick={onUsePassword}>
          {t("account.code.usePassword")}
        </button>
      </div>
    </div>
  );
}
